import React, { useState, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { ReactComponent as NoData } from "assets/images/no-data.svg";
import styles from "./VirtualTransfer.module.scss";

const ITEM_HEIGHT = 40;
const LIST_HEIGHT = 220;

const generateMockData = (count) =>
  Array.from({ length: count }, (_, i) => ({
    key: i.toString(),
    title: `Item ${i + 1}`,
  }));

const ListCard = ({
  items,
  selectedItems,
  setSelectedItems,
  headerActions,
  disabled,
}) => {
  const handleSelect = (key, setSelectedKeys) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const handleSelectAll = (items, setSelectedKeys) => {
    setSelectedKeys((prev) =>
      prev.length === items.length ? [] : items.map((item) => item.key)
    );
  };
  const renderRow =
    (items, selectedKeys, setSelectedKeys) =>
    ({ index, style }) => {
      const item = items[index];
      const isChecked = selectedKeys.includes(item.key);
      return (
        <div
          key={item.key}
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            padding: "0 10px",
            borderBottom: "1px solid #eee",
            backgroundColor: isChecked ? "#e9f7fe" : "#fff",
          }}
        >
          <input
            type="checkbox"
            className="form-check-input"
            checked={isChecked}
            onChange={() => handleSelect(item.key, setSelectedKeys)}
            disabled={disabled}
          />
          <span className="ms-2">{item.title}</span>
        </div>
      );
    };
  return (
    <div className={`${styles["list-card"]}`}>
      <div className={`${styles["header"]}`}>
        <div className={`${styles["header-select"]}`}>
          <input
            type="checkbox"
            className="form-check-input"
            onClick={() => handleSelectAll(items, setSelectedItems)}
            checked={items.length && selectedItems.length === items.length}
            disabled={disabled}
          />
          <span>
            {selectedItems.length > 0
              ? `${selectedItems.length} / ${items.length} item`
              : `${items.length} item`}
          </span>
        </div>
        {headerActions && (
          <div className={`${styles["header-actions"]}`}>{headerActions}</div>
        )}
      </div>
      {items.length ? (
        <List
          height={LIST_HEIGHT}
          itemCount={items.length}
          itemSize={ITEM_HEIGHT}
        >
          {renderRow(items, selectedItems, setSelectedItems)}
        </List>
      ) : (
        <div className={`${styles["empty-list"]}`}>
          <NoData />
          <span>No data</span>
        </div>
      )}
    </div>
  );
};

export const VirtualTransfer = ({
  data,
  sourceHeader,
  targetHeader,
  sourceHeaderActions,
  onTargetKeysChanged,
  targetKeys,
  disabled,
}) => {
  const [selectedSourceKeys, setSelectedSourceKeys] = useState([]);
  const [selectedTargetKeys, setSelectedTargetKeys] = useState([]);

  const sourceItems = useMemo(
    () => data.filter((item) => !targetKeys.includes(item.key)),
    [data, targetKeys]
  );

  const targetItems = useMemo(
    () => data.filter((item) => targetKeys.includes(item.key)),
    [data, targetKeys]
  );

  const moveToTarget = () => {
    const existingTargetKeys = targetItems.map((item) => item.key);
    onTargetKeysChanged([...existingTargetKeys, ...selectedSourceKeys]);
    setSelectedSourceKeys([]);
  };

  const moveToSource = () => {
    const existingTargetKeys = targetItems.map((item) => item.key);
    onTargetKeysChanged([
      ...existingTargetKeys.filter((k) => !selectedTargetKeys.includes(k)),
    ]);
    setSelectedTargetKeys([]);
  };

  return (
    <div className={`${styles["transfer-container"]}`}>
      {/* Source List */}
      <div className={`${styles["list-container"]}`}>
        <h5 className="text-primary">{sourceHeader}</h5>
        <ListCard
          items={sourceItems}
          selectedItems={selectedSourceKeys}
          setSelectedItems={setSelectedSourceKeys}
          headerActions={sourceHeaderActions}
          disabled={disabled}
        />
      </div>
      <div className={`${styles["transfer-operation"]}`}>
        <button
          className="btn btn-green"
          onClick={moveToTarget}
          disabled={!selectedSourceKeys.length}
        >
          →
        </button>
        <button
          className="btn btn-green"
          onClick={moveToSource}
          disabled={!selectedTargetKeys.length}
        >
          ←
        </button>
      </div>

      {/* Target List */}
      <div className={`${styles["list-container"]}`}>
        <h5 className="text-primary">{targetHeader}</h5>
        <ListCard
          items={targetItems}
          selectedItems={selectedTargetKeys}
          setSelectedItems={setSelectedTargetKeys}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
